/**
 * AtlasForge Partner Portal — API client.
 *
 * The Atlas v1 API runs on the SAME backend as the marketing site.
 * In preview that's the preview host;
 * in production it's `atlasforgefinancial.com`. We pick that up from
 * REACT_APP_BACKEND_URL, with REACT_APP_ATLAS_API_BASE as an optional override
 * for partners deploying their own portal against a different host.
 *
 * Auth: pure X-API-Key. Partner pastes `afg_test_*` (sandbox) or `afg_live_*` (production)
 * on /portal/login. Key is stored in localStorage. Ingest secret is fetched ONCE per
 * session via GET /api/v1/secrets and cached in memory only.
 */

const BASE = (
  process.env.REACT_APP_ATLAS_API_BASE ||
  process.env.REACT_APP_BACKEND_URL ||
  ""
).replace(/\/+$/, "");

const KEY_STORAGE = "atlas_partner_key";

export class AtlasApiError extends Error {
  constructor(status, payload) {
    super(payload?.detail?.message || payload?.detail || payload?.message || `HTTP ${status}`);
    this.status = status;
    this.payload = payload;
  }
}

let _ingestSecret = null;

export function getStoredKey() {
  try { return localStorage.getItem(KEY_STORAGE); } catch { return null; }
}

export function setStoredKey(k) {
  try { localStorage.setItem(KEY_STORAGE, k); } catch { /* ignore */ }
  _ingestSecret = null; // re-fetch with new key
}

export function clearStoredKey() {
  try { localStorage.removeItem(KEY_STORAGE); } catch { /* ignore */ }
  _ingestSecret = null;
}

export function keyMode(key = getStoredKey()) {
  if (!key) return null;
  if (key.startsWith("afg_live_")) return "production";
  if (key.startsWith("afg_test_")) return "sandbox";
  return "unknown";
}

export function signOut() {
  clearStoredKey();
  if (typeof window !== "undefined") {
    window.location.href = "/portal/login";
  }
}

export async function getIngestSecret() {
  if (_ingestSecret) return _ingestSecret;
  const key = getStoredKey();
  if (!key) throw new AtlasApiError(401, { message: "Not signed in" });
  const res = await fetch(`${BASE}/api/v1/secrets`, {
    headers: { "X-API-Key": key },
  });
  if (!res.ok) {
    if (res.status === 401) signOut();
    const data = await res.json().catch(() => ({}));
    throw new AtlasApiError(res.status, data);
  }
  const data = await res.json();
  _ingestSecret = data.ingest_secret;
  return _ingestSecret;
}

export async function atlasFetch(path, opts = {}) {
  const key = getStoredKey();
  if (!key) {
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/portal/login")) {
      window.location.href = "/portal/login";
    }
    throw new AtlasApiError(401, { message: "Not signed in" });
  }

  const headers = {
    "Content-Type": "application/json",
    "X-API-Key": key,
    ...(opts.headers || {}),
  };

  if (opts.needsIngestSecret) {
    headers["X-Atlas-Ingest-Secret"] = await getIngestSecret();
  }

  // Strip non-fetch opts before passing to fetch().
  const { skipAutoSignOut, needsIngestSecret: _ns, ...fetchOpts } = opts;

  let res;
  try {
    res = await fetch(`${BASE}${path}`, { ...fetchOpts, headers });
  } catch (e) {
    throw new AtlasApiError(0, {
      message: `Couldn't reach the API at ${BASE}. Check your network or CORS.`,
    });
  }

  let data = {};
  try { data = await res.json(); } catch { /* may be empty */ }

  if (!res.ok) {
    if (res.status === 401 && !skipAutoSignOut) {
      signOut();
    }
    if (res.status === 429) {
      const retryAfter = res.headers.get("Retry-After");
      throw new AtlasApiError(429, { message: `Rate limited. Retry after ${retryAfter || "a moment"}s.`, retryAfter });
    }
    throw new AtlasApiError(res.status, data);
  }
  return data;
}

/** Convenience: the configured base URL — useful for building integration-guide links. */
export const ATLAS_BASE = BASE;
